import { useState, useEffect } from "react";
import { Receipt, Download, CheckCircle, Clock, XCircle, Gauge, Trash2, Search, FileX, FileDown, Plus, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { generateInvoicePDF } from "../utils/pdfGenerator";
import { archivePdf } from "../utils/archiveService";
import { authFetch } from "../services/authService";
import DirectInvoiceForm from "../components/DirectInvoiceForm";
import XmlValidatorDialog from "../components/XmlValidatorDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invoices({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [creditNoteDialog, setCreditNoteDialog] = useState(null);
  const [creditNoteReason, setCreditNoteReason] = useState("");
  const [showDirectForm, setShowDirectForm] = useState(false);
  const [validatorInvoice, setValidatorInvoice] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Filter invoices based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredInvoices(invoices);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = invoices.filter(inv => 
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.customer_name?.toLowerCase().includes(term) ||
        inv.vehicle_plate?.toLowerCase().includes(term) ||
        inv.quote_number?.toLowerCase().includes(term)
      );
      setFilteredInvoices(filtered);
    }
  }, [searchTerm, invoices]);

  const fetchInvoices = async () => {
    try {
      const response = await authFetch(`${API}/invoices`);
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
        setFilteredInvoices(data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentStatusChange = async (invoiceId, paymentStatus) => {
    try {
      const response = await authFetch(`${API}/invoices/${invoiceId}/payment-status?payment_status=${paymentStatus}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato pagamento aggiornato");
        fetchInvoices();
        if (selectedInvoice) {
          setSelectedInvoice({...selectedInvoice, payment_status: paymentStatus});
        }
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const downloadXML = async (invoiceId) => {
    try {
      const response = await authFetch(`${API}/invoices/${invoiceId}/xml`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `fattura_${invoiceId}.xml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("File XML scaricato");
      }
    } catch (error) {
      toast.error("Errore nel download");
    }
  };

  // Professional PDF export using pdfGenerator
  const exportToPDF = (invoice) => {
    const doc = generateInvoicePDF(invoice, user);
    doc.save(`Fattura_${invoice.invoice_number}_${new Date().toISOString().split('T')[0]}.pdf`);
    // Archivia copia immutabile (in background)
    archivePdf(doc.output('blob'), "invoice", invoice.invoice_id, invoice.invoice_number);
    toast.success("PDF scaricato");
  };

  // Delete invoice with number reassignment
  const handleDeleteInvoice = async (invoiceId) => {
    try {
      const response = await authFetch(`${API}/invoices/${invoiceId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Fattura eliminata e numeri riassegnati");
        setSelectedInvoice(null);
        setDeleteConfirm(null);
        fetchInvoices();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Create credit note
  const handleCreateCreditNote = async (invoice) => {
    try {
      const response = await authFetch(`${API}/invoices/${invoice.invoice_id}/credit-note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: creditNoteReason })
      });

      if (response.ok) {
        // Download the XML file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `nota_credito_${invoice.invoice_number.replace("/", "-")}.xml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success("Nota di Credito creata e scaricata");
        setCreditNoteDialog(null);
        setCreditNoteReason("");
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore nella creazione della nota di credito");
    }
  };

  const paymentStatusColors = {
    pending: "status-pending",
    partial: "status-in_progress",
    paid: "status-paid"
  };

  const paymentStatusLabels = {
    pending: "In Attesa",
    partial: "Parziale",
    paid: "Pagato"
  };

  const paymentStatusIcons = {
    pending: Clock,
    partial: XCircle,
    paid: CheckCircle
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="invoices-page" className="space-y-6">
      {/* Header */}
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Fatture</h1>
          <p className="text-zinc-500">Gestione fatture emesse e dirette</p>
        </div>
        <Button
          data-testid="new-direct-invoice-btn"
          className="btn-primary"
          onClick={() => setShowDirectForm(true)}
        >
          <Plus className="w-4 h-4 mr-2" /> Nuova Fattura
        </Button>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Cerca per numero fattura, cliente, targa, preventivo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="search-invoices"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-400" />
            <div>
              <div className="kpi-value">{invoices.filter(i => i.payment_status === "pending").length}</div>
              <div className="kpi-label">In Attesa</div>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <div>
              <div className="kpi-value">{invoices.filter(i => i.payment_status === "paid").length}</div>
              <div className="kpi-label">Pagate</div>
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-3">
            <Receipt className="w-8 h-8 text-primary" />
            <div>
              <div className="kpi-value font-['JetBrains_Mono']">
                € {invoices.filter(i => i.payment_status === "paid").reduce((sum, i) => sum + i.total, 0).toFixed(2)}
              </div>
              <div className="kpi-label">Incassato</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Data</th>
                <th>Cliente</th>
                <th>Veicolo</th>
                <th className="text-right">Totale</th>
                <th>Stato Pagamento</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => {
                const StatusIcon = paymentStatusIcons[invoice.payment_status];
                return (
                  <tr key={invoice.invoice_id} className="cursor-pointer" onClick={() => setSelectedInvoice(invoice)}>
                    <td className="font-['JetBrains_Mono'] text-primary">#{invoice.invoice_number}</td>
                    <td className="font-['JetBrains_Mono']">{invoice.created_at?.split("T")[0]}</td>
                    <td>{invoice.customer_name}</td>
                    <td>{invoice.vehicle_plate}</td>
                    <td className="text-right font-['JetBrains_Mono'] font-bold">€ {invoice.total.toFixed(2)}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs uppercase ${paymentStatusColors[invoice.payment_status]}`}>
                        <StatusIcon className="w-3 h-3" />
                        {paymentStatusLabels[invoice.payment_status]}
                      </span>
                    </td>
                    <td className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          exportToPDF(invoice);
                        }}
                        className="text-primary hover:text-primary/80"
                        data-testid="invoice-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setValidatorInvoice(invoice);
                        }}
                        className="text-green-500 hover:text-green-400"
                        data-testid="validate-xml-btn"
                        title="Valida e Scarica XML"
                      >
                        <ShieldCheck className="w-4 h-4 mr-1" />
                        XML
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreditNoteDialog(invoice);
                        }}
                        className="text-orange-500 hover:text-orange-400"
                        data-testid="credit-note-btn"
                        title="Crea Nota di Credito"
                      >
                        <FileX className="w-4 h-4 mr-1" />
                        NC
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Receipt className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">{searchTerm ? "Nessun risultato" : "Nessuna fattura"}</p>
          <p className="empty-state-text">{searchTerm ? "Prova con altri termini" : "Crea una fattura dal preventivo o usa il pulsante 'Nuova Fattura'"}</p>
        </div>
      )}

      {/* Detail Dialog */}
      {selectedInvoice && (
        <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center justify-between">
                <span>Fattura #{selectedInvoice.invoice_number}</span>
                <Button
                  onClick={() => setDeleteConfirm(selectedInvoice.invoice_id)}
                  variant="outline"
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>
            
            {/* Delete Confirmation */}
            {deleteConfirm && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-4">
                <p className="text-red-400 mb-3">Confermi l'eliminazione di questa fattura? I numeri saranno riassegnati e il preventivo tornerà allo stato "Accettato".</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDeleteInvoice(deleteConfirm)}
                    size="sm"
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Conferma Eliminazione
                  </Button>
                  <Button
                    onClick={() => setDeleteConfirm(null)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700"
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between">
                <div className="text-zinc-500">
                  Da Preventivo #{selectedInvoice.quote_number}
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    value={selectedInvoice.payment_status}
                    onValueChange={(value) => handlePaymentStatusChange(selectedInvoice.invoice_id, value)}
                  >
                    <SelectTrigger className="w-40 bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="pending">In Attesa</SelectItem>
                      <SelectItem value="partial">Parziale</SelectItem>
                      <SelectItem value="paid">Pagato</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => exportToPDF(selectedInvoice)}
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                  <Button
                    onClick={() => downloadXML(selectedInvoice.invoice_id)}
                    className="btn-primary"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    XML
                  </Button>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 text-sm bg-zinc-950 rounded-sm p-4">
                <div>
                  <span className="text-zinc-500">Cliente:</span>
                  <span className="text-white ml-2">{selectedInvoice.customer_name}</span>
                </div>
                <div>
                  <span className="text-zinc-500">CF:</span>
                  <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedInvoice.customer_fiscal_code}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Email:</span>
                  <span className="text-white ml-2">{selectedInvoice.customer_email}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Telefono:</span>
                  <span className="text-white ml-2">{selectedInvoice.customer_phone}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-zinc-500">Indirizzo:</span>
                  <span className="text-white ml-2">{selectedInvoice.customer_address}</span>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">Veicolo:</span>
                  <span className="text-white ml-2">{selectedInvoice.vehicle_model}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Targa:</span>
                  <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedInvoice.vehicle_plate}</span>
                </div>
                {selectedInvoice.vehicle_km > 0 && (
                  <div className="col-span-2">
                    <span className="text-zinc-500 flex items-center gap-1">
                      <Gauge className="w-4 h-4" />
                      Kilometri:
                    </span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedInvoice.vehicle_km.toLocaleString()} km</span>
                  </div>
                )}
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th>Descrizione</th>
                      <th>Marca</th>
                      <th className="text-right">Qtà</th>
                      <th className="text-right">Prezzo</th>
                      <th className="text-right">IVA</th>
                      <th className="text-right">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.description}</td>
                        <td>{item.brand || "-"}</td>
                        <td className="text-right font-['JetBrains_Mono']">{item.quantity}</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {item.unit_price.toFixed(2)}</td>
                        <td className="text-right font-['JetBrains_Mono']">{item.vat_rate}%</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(item.subtotal + item.vat).toFixed(2)}</td>
                      </tr>
                    ))}
                    {selectedInvoice.labor_hours > 0 && (
                      <tr>
                        <td>Manodopera</td>
                        <td>-</td>
                        <td className="text-right font-['JetBrains_Mono']">{selectedInvoice.labor_hours}h</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {selectedInvoice.labor_cost.toFixed(2)}/h</td>
                        <td className="text-right font-['JetBrains_Mono']">22%</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(selectedInvoice.labor_cost * selectedInvoice.labor_hours * 1.22).toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-500">Imponibile:</span>
                  <span className="text-white font-['JetBrains_Mono']">€ {selectedInvoice.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-500">IVA:</span>
                  <span className="text-white font-['JetBrains_Mono']">€ {selectedInvoice.vat_total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl pt-2 border-t border-zinc-800">
                  <span className="text-white font-bold">TOTALE:</span>
                  <span className="text-primary font-bold font-['JetBrains_Mono']">€ {selectedInvoice.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Credit Note Dialog */}
      {creditNoteDialog && (
        <Dialog open={!!creditNoteDialog} onOpenChange={() => { setCreditNoteDialog(null); setCreditNoteReason(""); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                <FileX className="w-5 h-5 text-orange-500" />
                Crea Nota di Credito
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <p className="text-zinc-400 text-sm mb-2">Fattura di riferimento:</p>
                <p className="text-white font-['JetBrains_Mono'] text-lg">#{creditNoteDialog.invoice_number}</p>
                <p className="text-zinc-500 mt-1">{creditNoteDialog.customer_name}</p>
                <p className="text-primary font-bold mt-2">€ {creditNoteDialog.total.toFixed(2)}</p>
              </div>

              <div className="form-group">
                <label className="form-label">Motivazione *</label>
                <Textarea
                  value={creditNoteReason}
                  onChange={(e) => setCreditNoteReason(e.target.value)}
                  placeholder="Inserisci la motivazione per la nota di credito..."
                  className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                  data-testid="credit-note-reason"
                />
              </div>

              <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-3">
                <p className="text-orange-400 text-sm">
                  <strong>Attenzione:</strong> Verrà generata una nota di credito elettronica in formato XML 
                  per l'intero importo della fattura.
                </p>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button 
                variant="ghost" 
                onClick={() => { setCreditNoteDialog(null); setCreditNoteReason(""); }}
                className="text-zinc-400"
              >
                Annulla
              </Button>
              <Button
                onClick={() => handleCreateCreditNote(creditNoteDialog)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                disabled={!creditNoteReason.trim()}
                data-testid="confirm-credit-note-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Genera Nota di Credito
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Direct Invoice Form */}
      {showDirectForm && (
        <DirectInvoiceForm
          onClose={() => setShowDirectForm(false)}
          onCreated={() => fetchInvoices()}
        />
      )}

      {/* XML Validator Dialog */}
      {validatorInvoice && (
        <XmlValidatorDialog
          invoiceId={validatorInvoice.invoice_id}
          invoiceNumber={validatorInvoice.invoice_number}
          onClose={() => setValidatorInvoice(null)}
          onDownload={() => downloadXML(validatorInvoice.invoice_id)}
        />
      )}
    </div>
  );
}
